import Vue from 'vue'
import VueRouter from 'vue-router'
import { request } from '@/utils/request'
import { getToken, removeToken } from '@/utils/auth'
import { updateSeo, updateCanonical, updateNoindex, injectBrandJsonLd } from '@/utils/seo'

Vue.use(VueRouter)

// 设备检测工具函数
function isDesktop() {
    // 根据屏幕宽度判断（更可靠）
    const width = window.innerWidth || document.documentElement.clientWidth
        // 也可以结合 UA 判断
    const ua = navigator.userAgent.toLowerCase()
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)

    // 宽度 >= 1024 且不是移动端 UA，则认为是 PC
    return width >= 1024 && !isMobileUA
}

// 动态加载组件的工厂函数
// 使用明确的路径映射，确保 webpack 能正确处理
const componentMap = {
    'Home': {
        pc: () =>
            import ('@/views/pc/Home.vue'),
        h5: () =>
            import ('@/views/h5/Home.vue')
    },
    'Detail': {
        pc: () =>
            import ('@/views/pc/Detail.vue'),
        h5: () =>
            import ('@/views/h5/Detail.vue')
    },
    'About': {
        pc: () =>
            import ('@/views/pc/About.vue'),
        h5: () =>
            import ('@/views/h5/About.vue')
    },
    'UserAgreement': {
        pc: () =>
            import ('@/views/pc/UserAgreement.vue'),
        h5: () =>
            import ('@/views/h5/UserAgreement.vue')
    },
    'Test': {
        pc: () =>
            import ('@/views/pc/test.vue')
    },
	'Page': {
	    pc: () =>
	        import ('@/views/pc/Page.vue'),
	    h5: () =>
	        import ('@/views/h5/Page.vue')
	},
	'Favorites': {
	    pc: () =>
	        import ('@/views/pc/Favorites.vue'),
	    h5: () =>
	        import ('@/views/h5/Favorites.vue')
	},
	'Redirect': {
	    pc: () =>
	        import ('@/views/pc/Redirect.vue'),
	    h5: () =>
	        import ('@/views/h5/Redirect.vue')
	},
	'Faq': {
	    pc: () =>
	        import ('@/views/pc/Faq.vue'),
	    h5: () =>
	        import ('@/views/h5/Faq.vue')
	},
	'Blog': {
	    pc: () =>
	        import ('@/views/pc/Blog.vue'),
	    h5: () =>
	        import ('@/views/h5/Blog.vue')
	},
	'BlogDetail': {
	    pc: () =>
	        import ('@/views/pc/BlogDetail.vue'),
	    h5: () =>
	        import ('@/views/h5/BlogDetail.vue')
	}
}

function loadComponent(componentName) {
    return () => {
        const components = componentMap[componentName]
        if (!components) {
            // 如果组件不存在，回退到 H5 版本
            return import (`@/views/h5/${componentName}.vue`)
        }

        if (isDesktop()) {
            // PC 版本，如果不存在则回退到 H5
            return components.pc().catch(() => components.h5())
        } else {
            // H5 版本
            return components.h5()
        }
    }
}

const routes = [{
        path: '/',
        name: 'Home',
        component: loadComponent('Home'),
        meta: {
			  seoKey: 'home'
        }
    },
    {
        path: '/detail/:id',
        name: 'Detail',
        component: loadComponent('Detail'),
        meta: {
			seoKey: 'detail',
			hideFooter: true
        }
    },
    {
        path: '/about',
        name: 'About',
        component: loadComponent('About'),
        meta: {
            seoKey: 'about',
            hideHeader: true,
            hideFooter: true
        }
    },
    {
        path: '/user-agreement',
        name: 'UserAgreement',
        component: loadComponent('UserAgreement'),
        meta: {
            seoKey: 'userAgreement',
            hideFooter: true
        }
    },
	{
	    path: '/page/:type',
	    name: 'Page',
	    component: loadComponent('Page'),
	    meta: {
            seoKey: 'page',
            hideFooter: true
	    }
	},
	{
	    path: '/Favorites',
	    name: 'Favorites',
	    component: loadComponent('Favorites'),
	    meta: {
	        seoKey: 'favorites',
	        hideFooter: true
	    }
	},  
	{
		  path: '/redirect',
		  name: 'Redirect',
		 component: loadComponent('Redirect'),
	 meta: {
		     hideFooter: true
		 }
	},
	{
		  path: '/faq',
		  name: 'Faq',
		 component: loadComponent('Faq'),
		 meta: {
	        seoKey: 'faq',
	        hideFooter: true
		 }
	},
	{
		  path: '/blog',
		  name: 'Blog',
		 component: loadComponent('Blog'),
		 meta: {
	        seoKey: 'blog',
	        hideFooter: true
		 }
	},
	{
		  path: '/blog/:id',
		  name: 'BlogDetail',
		 component: loadComponent('BlogDetail'),
		 meta: {
	        seoKey: 'blogDetail',
	        hideFooter: true
		 }
	}
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
        // 如果有保存的位置（比如浏览器前进后退），则返回保存的位置
        if (savedPosition) {
            return savedPosition
        } else {
            // 否则滚动到顶部
            return { x: 0, y: 0 }
        }
    }
})

// 路由守卫 - 设置页面标题 & 校验 token 是否失效
router.beforeEach((to, from, next) => {
     // document.title = to.meta.title || 'China Auto'
    // 更新页面 SEO（通用页面用，详情页在组件中单独设置）
    updateSeo(to)
	// 注入品牌实体结构化数据（Organization + WebSite），四语言站 origin 自适应
	injectBrandJsonLd()
	// 更新 canonical 标签（SEO 主页面统一注入）
	updateCanonical(to)
	// 收藏页/跳转页等非索引页写入 noindex（离开时自动清除）
	updateNoindex(to)
	
    if (from.name === 'About') {
        if (window.destroyFullpage && typeof window.destroyFullpage === 'function') {
            window.destroyFullpage();
        }
    }

    // 不阻塞导航，异步校验 token 是否失效
    const token = getToken()
    if (!token) {
        next()
        return
    }

    next()

    request.get('/auth/check_status').then(res => {
        if (res && res.data && res.data.is_logged_in === false) {
            removeToken()
        }
    }).catch(() => {
        removeToken()
    })
})

router.afterEach((to, from) => {
    // 只统计页面跳转，不统计API请求
    if (to.path !== from.path) {
        fetch('/api/getSiteInfo/seoinfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                // url: to.fullPath,
                // referrer: from.fullPath,
                // timestamp: Date.now()
            })
        })
    }
})
export default router